import { captureException } from "../../../utils/sentry/lazy-sentry.js";
import { rpc } from "../../AuthWrapper2/rpc-bridge.js";
import { AuthViewModel } from "../../AuthWrapper2/viewmodels/AuthViewModel.js";
import { rpcHookRefreshSignal } from "../../../hooks/useRPC/index.js";
import { ViewModel, singleton } from "mvc-kit";
//#region src/modules/AppWrapper2/viewmodels/RegionViewModel.ts
var RegionViewModel = class extends ViewModel {
	static DEFAULT_STATE = {
		allowMultiRegionSelection: false,
		currentRegionId: null,
		initialized: false,
		initializedForUserId: null,
		scopeAllRegions: false,
		selectedRegionIds: []
	};
	authVM = singleton(AuthViewModel);
	get availableRegions() {
		return this.authVM.regions;
	}
	get currentRegion() {
		return this.availableRegions.find((r) => r.id === this.state.currentRegionId);
	}
	get selectedRegions() {
		return this.state.selectedRegionIds.map((id) => this.availableRegions.find((r) => r.id === id)).filter(Boolean);
	}
	get hasRegions() {
		return !!this.currentRegion && this.availableRegions.length > 0;
	}
	get isMultiRegion() {
		return this.state.allowMultiRegionSelection;
	}
	/**
	* The region ids every region-scoped backend call should be scoped to — the
	* user's selection, or every accessible region when `scopeAllRegions` is on
	* (Portal). Both the RPC identity metadata and the downloads-api headers are
	* derived from this, so they can't drift apart.
	*/
	get scopedRegionIds() {
		return this.state.scopeAllRegions ? this.availableRegions.map((r) => r.id) : this.state.selectedRegionIds;
	}
	get userPrimaryRegionId() {
		return this.authVM.currentUser?.primaryRegionId;
	}
	onInit() {
		const authVM = this.authVM;
		this.subscribeTo(authVM, () => {
			const currentUserId = authVM.currentUser?.id ?? null;
			if (!authVM.state.identityInfo) return;
			if (currentUserId !== this.state.initializedForUserId) {
				this.initializeFromStorage();
				return;
			}
			this.revalidateSelection();
		});
		if (authVM.state.identityInfo) this.initializeFromStorage();
	}
	onSet(prev, next) {
		if (!next.initialized) return;
		const userId = this.authVM.currentUser?.id;
		if (!userId) return;
		if (prev.currentRegionId !== next.currentRegionId && next.currentRegionId) {
			const region = this.availableRegions.find((r) => r.id === next.currentRegionId);
			if (region) sessionStorage.setItem(`current_region_${userId}`, JSON.stringify(region));
		}
		if (prev.selectedRegionIds !== next.selectedRegionIds) {
			const selectedRegions = next.selectedRegionIds.map((id) => this.availableRegions.find((r) => r.id === id)).filter(Boolean);
			const multiKey = next.allowMultiRegionSelection ? "multi" : "single";
			sessionStorage.setItem(`selected_regions_${multiKey}_${userId}`, JSON.stringify(selectedRegions));
		}
	}
	changeRegion(regionId) {
		const region = this.availableRegions.find((r) => r.id === regionId);
		if (!region) {
			console.error(`Region with id "${regionId}" not found in indentiyInfo:`, this.availableRegions);
			captureException(/* @__PURE__ */ new Error(`Region with id "${regionId}" not found in identityInfo`), { extra: { identityInfoRegionIds: this.availableRegions.map((r) => r.id) } });
			return;
		}
		let nextSelectedIds;
		let nextCurrentId;
		if (!this.state.allowMultiRegionSelection) {
			nextSelectedIds = [region.id];
			nextCurrentId = region.id;
		} else if (this.state.selectedRegionIds.includes(region.id)) {
			const filtered = this.state.selectedRegionIds.filter((id) => id !== region.id);
			if (filtered.length === 0) return;
			nextSelectedIds = filtered;
			nextCurrentId = filtered[0];
		} else {
			nextSelectedIds = [...this.state.selectedRegionIds, region.id];
			nextCurrentId = this.state.currentRegionId || nextSelectedIds[0];
		}
		this.set({
			currentRegionId: nextCurrentId,
			selectedRegionIds: nextSelectedIds
		});
		this.syncRpcMetadata();
		const firstSelected = this.availableRegions.find((r) => r.id === nextSelectedIds[0]);
		if (firstSelected) this.emit("regionChanged", firstSelected);
	}
	/**
	* Push the host app's region config in. This MUST land before init() runs,
	* because initializeFromStorage() derives the sessionStorage bucket from
	* `allowMultiRegionSelection` and the RPC region scope from `scopeAllRegions`
	* — at their defaults it would read the wrong bucket and scope the session to
	* a single region. AppShellBootstrap therefore calls this synchronously in its
	* render body, before useSingleton() schedules onInit().
	*
	* If the config does arrive (or change) after init anyway, re-derive whatever
	* it invalidated rather than silently keeping the wrong values.
	*/
	configure(config) {
		const multiChanged = this.state.allowMultiRegionSelection !== config.allowMultiRegionSelection;
		const scopeChanged = this.state.scopeAllRegions !== config.scopeAllRegions;
		if (!multiChanged && !scopeChanged) return;
		this.set({
			allowMultiRegionSelection: config.allowMultiRegionSelection,
			scopeAllRegions: config.scopeAllRegions
		});
		if (!this.state.initialized) return;
		if (multiChanged) this.initializeFromStorage();
		else this.syncRpcMetadata();
	}
	revalidateSelection() {
		if (!this.state.initialized) return;
		const regions = this.availableRegions;
		if (!regions.length) return;
		const { currentRegionId, selectedRegionIds } = this.state;
		const validSelectedIds = selectedRegionIds.filter((id) => regions.some((r) => r.id === id));
		const currentIsValid = !!currentRegionId && regions.some((r) => r.id === currentRegionId);
		if (currentIsValid && validSelectedIds.length === selectedRegionIds.length) return;
		const fallbackRegion = regions.find((r) => r.id === this.userPrimaryRegionId) || regions[0];
		const nextSelectedIds = validSelectedIds.length ? validSelectedIds : [fallbackRegion.id];
		const nextCurrentId = currentIsValid && currentRegionId ? currentRegionId : nextSelectedIds[0];
		console.info("Removing selected region ids that are no longer available", { removedIds: selectedRegionIds.filter((id) => !validSelectedIds.includes(id)) });
		this.set({
			currentRegionId: nextCurrentId,
			selectedRegionIds: nextSelectedIds
		});
		this.syncRpcMetadata();
	}
	syncRpcMetadata() {
		const { rpcClient } = rpc();
		rpcClient.setIdentityMetadata({ regionIds: this.scopedRegionIds });
		rpcClient.clearCache();
		rpcHookRefreshSignal("Selected Regions changed, RPC cache cleared, signal refresh");
	}
	initializeFromStorage() {
		const authVM = this.authVM;
		const userId = authVM.currentUser?.id;
		const regions = authVM.regions;
		if (!userId || !regions.length) return;
		const urlParams = new URLSearchParams(window.location.search);
		const urlRegionId = urlParams.get("regionId");
		if (urlRegionId) {
			urlParams.delete("regionId");
			const newSearch = urlParams.toString();
			window.history.replaceState({}, "", newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname);
			const urlRegion = regions.find((r) => r.id === urlRegionId);
			if (urlRegion) {
				this.set({
					currentRegionId: urlRegion.id,
					initialized: true,
					initializedForUserId: userId,
					selectedRegionIds: [urlRegion.id]
				});
				this.syncRpcMetadata();
				return;
			}
			console.warn(`URL Region ID "${urlRegionId}" not found in User Regions`);
		}
		const storageKey = `selected_regions_${this.state.allowMultiRegionSelection ? "multi" : "single"}_${userId}`;
		const currentRegionKey = `current_region_${userId}`;
		let storedCurrentRegion = null;
		let storedSelectedRegions = [];
		try {
			const raw = sessionStorage.getItem(currentRegionKey);
			if (raw) storedCurrentRegion = JSON.parse(raw);
		} catch {}
		try {
			const raw = sessionStorage.getItem(storageKey);
			if (raw) storedSelectedRegions = JSON.parse(raw);
		} catch {}
		const validSelectedIds = storedSelectedRegions.map((r) => r.id).filter((id) => regions.some((r) => r.id === id));
		const validCurrentId = storedCurrentRegion && regions.some((r) => r.id === storedCurrentRegion.id) ? storedCurrentRegion.id : null;
		if (validSelectedIds.length > 0) {
			this.set({
				currentRegionId: validCurrentId || validSelectedIds[0],
				initialized: true,
				initializedForUserId: userId,
				selectedRegionIds: validSelectedIds
			});
			this.syncRpcMetadata();
			return;
		}
		const defaultRegion = regions.find((r) => r.id === authVM.currentUser?.primaryRegionId) || regions[0];
		this.set({
			currentRegionId: defaultRegion.id,
			initialized: true,
			initializedForUserId: userId,
			selectedRegionIds: [defaultRegion.id]
		});
		this.syncRpcMetadata();
	}
};
//#endregion
export { RegionViewModel };

//# sourceMappingURL=RegionViewModel.js.map